import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Container, Row, Alert } from 'react-bootstrap';
import Helmet from 'react-helmet';
import Navigation from "./Navigation";
import Start from './Start';
import Home from './Home';
import header from '../img/bannerC4FTour.png';
import Login from "../user/Login";
import Signup from '../user/Signup';
import Finance from '../team/Finance';
import '../css/App.css';
import Chat from "./Chat";
import Profile from "../user/Profile";
import Manual from "./Manual";
import Inscription from "../race/Inscription";
import Training from "../team/Training";
import RaceWrapper from "../race/RaceWrapper";
import NationalChampionships from "../calendar/NationalChampionships";
import Transfer from "../team/Transfer";
import Team from "../team/Team";
import Calendar from "../calendar/Calendar";
import Statistics from "./Statistics";
import Admin from "../admin/Admin";
import FairplayCommittee from "../admin/FairplayCommittee";
import RaceEditor from "../admin/RaceEditor";
import { LocalizationContextProvider } from "./LocalizationContext";
import C4FErrorModal from "../common/C4FErrorModal";
import axios from "axios";
import config from "../etc/config.json";
import { isNumber, fetchJSONFile } from "../common/Helpers";
import NationsCommittee from "../admin/NationsCommittee";
import Notifications from "../user/Notifications";
import Sandbox from "../admin/Sandbox";
import InfoModal from "./InfoModal";
import PollModal from "./PollModal";
import moment from "moment";

export const INIT_STATUS_NOT_STARTED = 0;
export const INIT_STATUS_IN_PROGRESS = 1;
export const INIT_STATUS_COMPLETED = 2;

// set the defaults
export const UserInformationContext = React.createContext({
  userInformation: null,
  setUserInformation: () => { },
  sessionInformation: null
});
export const AdminUserInformationContext = React.createContext({
  adminUserInformation: null,
  setAdminUserInformation: () => { }
});
export const SitterUserInformationContext = React.createContext({
  sitterUserInformation: null,
  setSitterUserInformation: () => { }
});
export const EventContext = React.createContext({
  events: null,
  setEvents: () => { }
});
export const ReferenceDataContext = React.createContext({
  commonRaceRules: null,
  countries: null,
  divisions: null,
  notificationTypes: null,
  prizeCalculationRules: null,
  raceEventRules: null,
  raceRulesets: null,
  recurringEvents: null,
  ruleTypes: null,
  raceEngine: null,
  localizationCommon: null, 
  localizationNavigation: null, 
  localizationRace: null, 
  localizationReferenceData: null, 
  localizationTransfermarket: null
});
export const C4FStatusContext = React.createContext({
  initialized: {},
  browserInfo: {},
  action: {},
  handleAction: () => { },
  showBanner: {},
  setShowBanner: () => { },
  language: {},
  setLanguage: () => { },
  messages: [],
  addMessage: () => { },
  raiseError: () => { },
  handleBackendRequest: () => { },
  handleBackendScript: () => { }
});
export const InfoModalContext = React.createContext({
  showInfo: {},
  showTeamInfo: {},
  showRiderInfo: {}
});
export const PollContext = React.createContext({
  polls: [],
  setPolls: () => {},
  refreshPolls: () => {},
  showPoll: () => {}
});

const ProtectedRoute = ({
  user,
  redirectPath = '/login',
  children,
}) => {
  if (user === null) {
    return <Navigate to={redirectPath} replace />
  }
  return children;
};


function App(props) {
  const [initialized, setInitialized] = useState(INIT_STATUS_NOT_STARTED);
  const [messages, setMessages] = useState([{ message: 'loaded', type: 'info', date: new Date() }]);
  const [error, setError] = useState(null);
  const [browserInfo, setBrowserInfo] = useState(null);
  const [action, setAction] = useState('start');
  const [showBanner, setShowBanner] = useState(true);
  const [language, setLanguage] = useState('EN');
  const [refresh, doRefresh] = useState(0);
  const [events, setEvents] = useState(null);
  
  const [userInformation, setUserInformation] = useState(null);
  const [sessionInformation, setSessionInformation] = useState(null);
  const [adminUserInformation, setAdminUserInformation] = useState(null);
  const [sitterUserInformation, setSitterUserInformation] = useState(null);
  const [selectedInfoObject, setSelectedInfoObject] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedPollId, setSelectedPollId] = useState(null);
  const [polls, setPolls] = useState(null);
  const [showPollModal, setShowPollModal] = useState(false);

  const navigate = useNavigate();
  // reference data context
  const [commonRaceRules, setCommonRaceRules] = useState(null);
  const [countries, setCountries] = useState(null);
  const [divisions, setDivisions] = useState(null);
  const [notificationTypes, setNotificationTypes] = useState(null);
  const [prizeCalculationRules, setPrizeCalculationRules] = useState(null);
  const [raceEventRules, setRaceEventRules] = useState(null);
  const [raceRulesets, setRaceRulesets] = useState(null);
  const [recurringEvents, setRecurringEvents] = useState(null);
  const [ruleTypes, setRuleTypes] = useState(null);
  const [raceEngine, setRaceEngine] = useState(null);
  const [localizationCommon, setLocalizationCommon] = useState(null);
  const [localizationNavigation, setLocalizationNavigation] = useState(null);
  const [localizationRace, setLocalizationRace] = useState(null);
  const [localizationReferenceData, setLocalizationReferenceData] = useState(null);
  const [localizationTransfermarket, setLocalizationTransfermarket] = useState(null);
  const referenceDataValue = { 
    commonRaceRules,
    countries,
    divisions,
    notificationTypes,
    prizeCalculationRules,
    raceEventRules,
    raceRulesets,
    recurringEvents,
    ruleTypes,
    raceEngine,
    localizationCommon, 
    localizationNavigation, 
    localizationRace, 
    localizationReferenceData, 
    localizationTransfermarket
   };

  const eventValue = { events, setEvents };
  const userValue = { userInformation, setUserInformation, sessionInformation };
  const adminUserValue = { adminUserInformation, setAdminUserInformation };
  const sitterUserValue = { sitterUserInformation, setSitterUserInformation };

  const c4fStatusValue = {
    initialized,
    browserInfo,
    action,
    handleAction,
    showBanner,
    setShowBanner,
    language,
    setLanguage,
    messages,
    addMessage,
    raiseError,
    handleBackendRequest,
    handleBackendScript
  };

  const infoModalValue = {
    showInfo,
    showTeamInfo,
    showRiderInfo
  }

  const pollValue = {
    polls,
    setPolls,
    refreshPolls,
    showPoll
  }

  function isFatalError(errorCode) {
    if (isNumber(errorCode) && errorCode >= 1000 && errorCode <= 1007) return true;
    else return false;
  }

  function handleFatalError(errorCode, errorMessage) {
    console.log(errorCode + ': ' + errorMessage);
    raiseError('Fatal error', errorCode, errorMessage);
    handleLogout();
  }

  function handleBackendPositiveResponse(response, successCallback, suppressError = false, errorType, failureCallback, skipCallbackOnEmptyResult = true) {
    if (typeof response.data !== 'string' && 'errorCode' in response.data) {
      if (isFatalError(response.data.errorCode)) handleFatalError(response.data.errorCode, response.data.errorMessage);
      else if (!suppressError) raiseError(errorType, response.data.errorCode, response.data.errorMessage);
      if (failureCallback !== null && (response.data.constructor === Object || response.data.constructor === Array || !skipCallbackOnEmptyResult)) failureCallback(response.data);
    }
    else {
      processSessionInformation(response.headers);
      if (successCallback !== null && (response.data.constructor === Object || response.data.constructor === Array || !skipCallbackOnEmptyResult)) {
        try {
          successCallback(response.data);
        } catch (e) {
          console.log(e);
        }
      }
    }
  }

  function handleBackendNegativeResponse(response, suppressError = false, errorType, failureCallback, skipCallbackOnEmptyResult = true) {
    if (typeof response !== 'undefined' && response !== null) {
      if (typeof response.data !== 'string' && 'errorCode' in response.data) {
        if (isFatalError(response.data.errorCode)) handleFatalError(response.data.errorCode, response.data.errorMessage);
        else if (!suppressError) raiseError(errorType, response.data.errorCode, response.data.errorMessage);
      }
      if (failureCallback !== null && (response.data.constructor === Object || response.data.constructor === Array || !skipCallbackOnEmptyResult))
        try {
          failureCallback(response.data);
        } catch (e) {
          console.log(e);
        }
    }
  }

  function processSessionInformation(headers) {
    if ('x-c4f-session-expires' in headers) setSessionInformation(headers);
  }

  function handleBackendScript(
    requestMethod,
    path,
    params = null,
    callback = null) {

    if (requestMethod === 'POST') {
      axios.post(config.UTILS + path, null, { params: params, withCredentials: true })
        .then(result => {
          callback(result);
        }).catch(({ response }) => {
          console.log(response);
          callback(response);
        }).catch(e => {
          console.log(e);
        });
    }
  }

  function handleBackendRequest(
    requestMethod,
    path,
    payload = null,
    params = null,
    successCallback = null,
    skipCallbackOnEmptyResult = true,
    failureCallback = null,
    suppressError = false,
    errorType = 'Technical Error') {

    if (requestMethod === 'POST') {
      axios.post(config.REST_API + path, payload, { params: params, withCredentials: true })
        .then(result => {
          handleBackendPositiveResponse(result, successCallback, suppressError, errorType, failureCallback, skipCallbackOnEmptyResult);
        }).catch(({ response }) => {
          console.log(response);
          handleBackendNegativeResponse(response, suppressError, errorType, failureCallback, skipCallbackOnEmptyResult);
        }).catch(e => {
          console.log(e);
        });
    }

    if (requestMethod === 'PATCH') {
      axios.patch(config.REST_API + path, payload, { params: params, withCredentials: true })
        .then(result => {
          handleBackendPositiveResponse(result, successCallback, suppressError, errorType, failureCallback, skipCallbackOnEmptyResult);
        }).catch(({ response }) => {
          console.log(response);
          handleBackendNegativeResponse(response, suppressError, errorType, failureCallback, skipCallbackOnEmptyResult);
        }).catch(e => {
          console.log(e);
        });
    }

    if (requestMethod === 'DELETE') {
      axios.delete(config.REST_API + path, { params: params, withCredentials: true })
        .then(result => {
          handleBackendPositiveResponse(result, successCallback, suppressError, errorType, failureCallback, skipCallbackOnEmptyResult);
        }).catch(({ response }) => {
          handleBackendNegativeResponse(response, suppressError, errorType, failureCallback, skipCallbackOnEmptyResult);
        }).catch(e => {
          console.log(e);
        });

    }

    if (requestMethod === 'GET') {
      axios.get(config.REST_API + path, { params: params, withCredentials: true })
        .then(result => {
          handleBackendPositiveResponse(result, successCallback, suppressError, errorType, failureCallback, skipCallbackOnEmptyResult);
        }).catch(({ response }) => {
          handleBackendNegativeResponse(response, suppressError, errorType, failureCallback, skipCallbackOnEmptyResult);
        }).catch(e => {
          console.log(e);
        });
    }
  }

  function handleLogout() {
    setUserInformation(null);
    setAdminUserInformation(null);
    setSitterUserInformation(null);
    setSessionInformation(null);
    setEvents(null);
    navigate('/');
  }

  function handleExitAdmin() {
    setUserInformation(adminUserInformation);
    setAdminUserInformation(null);
    setSitterUserInformation(null);
    navigate('/home');
  }

  function handleExitSitter() {
    setUserInformation(sitterUserInformation);
    setSitterUserInformation(null);
    navigate('/home');
  }

  function handleAction(action) {
    if (action === 'logout') {
      handleLogout();
      return;
    }
    if (action === 'exitAdmin') {
      handleExitAdmin();
      return;
    }
    if (action === 'exitSitter') {
      handleExitSitter();
      return;
    }
    if (action === 'forum') {
      window.open('http://www.radsportfreaks.de/RSFForum', '_blank');
      return;
    }
    navigate('/' + action);
    setAction(action);
  }

  function raiseError(type, code, message) {
    console.trace();
    doRefresh(prev => prev + 1);
    addMessage(type + ' ' + code + ' ' + message, 'error');
    setError(
      <C4FErrorModal key={refresh} show={true}
        type={type}
        code={code}
        message={message}
      />);
  }

  function addMessage(message, type = 'info') {
    setMessages(messages.concat([{ message: message, type: type, date: new Date() }]));
  }

  function getBrowserDetails(navigator) {
    let userAgent = navigator.userAgent;
    let browserName = 'unknown';
    let platform = 'unknown';

    if (userAgent.indexOf("OPR") !== -1) browserName = 'Opera';
    else if (userAgent.indexOf("Edg") !== -1) browserName = 'Microsoft Edge';
    else if (userAgent.indexOf("MSIE") !== -1) browserName = 'Microsoft Internet Explorer';
    else if (userAgent.indexOf("Chrome") !== -1) browserName = 'Chrome';
    else if (userAgent.indexOf("Safari") !== -1) browserName = 'Safari';
    else if (userAgent.indexOf("Firefox") !== -1) browserName = 'Firefox';

    if (userAgent.indexOf("Windows NT 10") !== -1) platform = 'Win10';
    if (userAgent.indexOf("Mac OS") !== -1) platform = 'MacOS';

    // if ('platform' in navigator.userAgentData) platform = navigator.userAgentData.platform;

    axios.get(process.env.PUBLIC_URL + '/referenceData/BrowserCapabilities.json?' + moment().milliseconds()).then(result => {
      let browserSupportsUnicodeFlags = !result.data.some(bl =>
        (bl.browserName === '*' || bl.browserName === browserName) &&
        (bl.platform === '*' || bl.platform === platform) &&
        !bl.supportsUnicodeFlags);

      console.log({
        userAgent: userAgent,
        browserName: browserName,
        platform: platform,
        browserSupportsUnicodeFlags: browserSupportsUnicodeFlags
      });
      setBrowserInfo({
        userAgent: userAgent,
        browserName: browserName,
        platform: platform,
        browserSupportsUnicodeFlags: browserSupportsUnicodeFlags
      });
    });
  }

  function showTeamInfo(team) {
    let isRider = !('ownerAcronym' in team);
    let teamId = null;
    if (team.teamId) teamId = team.teamId;
    else if (team.regularTeamId) teamId = team.regularTeamId;
    else if (isRider && team.owner) teamId = team.owner;
    else if (team.id) teamId = team.id;

    let teamName = null;
    if (team.teamName) teamName = team.teamName;
    else if (team.regularTeamName) teamName = team.regularTeamName;
    else if (isRider && team.ownerName) teamName = team.ownerName;
    else if (team.name) teamName = team.name;
    showInfo('team', null, teamId, teamName);
  };

  function showRiderInfo(rider) {
    let teamId = null;
    if (rider.owner) teamId = rider.owner;
    else if (rider.teamId) teamId = rider.teamId;

    let riderId = null;
    if (rider.id) riderId = rider.id;
    else if (rider.riderId) riderId = rider.riderId;

    let riderName = null;
    if (rider.riderName) riderName = rider.riderName;
    else if (rider.lastNames) riderName = rider.firstNames + ' ' + rider.lastNames;
    else if (rider.name) riderName = rider.name;
    showInfo('rider', riderId, teamId, riderName);
  };

  function showInfo(type, riderId, teamId, name) {
    setShowInfoModal(true);
    setSelectedInfoObject({ type: type, riderId: riderId, teamId: teamId, name: name });
  }
  function showPoll(pollId = null) {
    setShowPollModal(true);
    setSelectedPollId(pollId);
  }
  function refreshPolls() {
    handleBackendRequest('GET', '/poll', null, { userName: userInformation.userName }, function (polls) {
      setPolls(polls && polls.constructor === Array ? polls : []);
    },
    false);
}

  useEffect(() => {
    if (initialized === INIT_STATUS_NOT_STARTED) {
      getBrowserDetails(navigator);
      // make the reference data call unique to always load fresh data rather than from the cache
      let millis = moment().valueOf();
      fetchJSONFile(process.env.PUBLIC_URL + '/referenceData/CommonRaceRules.json?' + millis, setCommonRaceRules);
      fetchJSONFile(process.env.PUBLIC_URL + '/referenceData/Countries.json?' + millis, setCountries);
      fetchJSONFile(process.env.PUBLIC_URL + '/referenceData/Divisions.json?' + millis, setDivisions);
      fetchJSONFile(process.env.PUBLIC_URL + '/referenceData/NotificationTypes.json?' + millis, setNotificationTypes);
      fetchJSONFile(process.env.PUBLIC_URL + '/referenceData/PrizeCalculationRules.json?' + millis, setPrizeCalculationRules);
      fetchJSONFile(process.env.PUBLIC_URL + '/referenceData/RaceEventRules.json?' + millis, setRaceEventRules);
      fetchJSONFile(process.env.PUBLIC_URL + '/referenceData/RaceRulesets.json?' + millis, setRaceRulesets);
      fetchJSONFile(process.env.PUBLIC_URL + '/referenceData/RecurringEvents.json?' + millis, setRecurringEvents);
      fetchJSONFile(process.env.PUBLIC_URL + '/referenceData/RuleTypes.json?' + millis, setRuleTypes);
      fetchJSONFile(process.env.PUBLIC_URL + '/referenceData/RaceEngine.json?' + millis, setRaceEngine);

      fetchJSONFile(process.env.PUBLIC_URL + '/localization/LocalizationCommon.json?' + millis, setLocalizationCommon);
      fetchJSONFile(process.env.PUBLIC_URL + '/localization/LocalizationNavigation.json?' + millis, setLocalizationNavigation);
      fetchJSONFile(process.env.PUBLIC_URL + '/localization/LocalizationRace.json?' + millis, setLocalizationRace);
      fetchJSONFile(process.env.PUBLIC_URL + '/localization/LocalizationReferenceData.json?' + millis, setLocalizationReferenceData);
      fetchJSONFile(process.env.PUBLIC_URL + '/localization/LocalizationTransfermarket.json?' + millis, setLocalizationTransfermarket);

      setInitialized(INIT_STATUS_IN_PROGRESS);
    }
    else if (initialized === INIT_STATUS_IN_PROGRESS) {
      if (commonRaceRules &&
        countries &&
        divisions &&
        notificationTypes &&
        prizeCalculationRules &&
        raceEventRules &&
        raceRulesets &&
        recurringEvents &&
        ruleTypes &&
        raceEngine &&
        localizationCommon &&
        localizationNavigation &&
        localizationRace &&
        localizationReferenceData &&
        localizationTransfermarket)
        setInitialized(INIT_STATUS_COMPLETED);
    }
  }, [initialized, commonRaceRules, countries, divisions, notificationTypes, 
    prizeCalculationRules, raceEventRules, raceRulesets, recurringEvents, ruleTypes, raceEngine,
    localizationCommon, localizationNavigation, localizationRace, localizationReferenceData, localizationTransfermarket]);

  useEffect(() => {
    if (userInformation) {
      refreshPolls();
    }  
  }, [userInformation]);    

  return (
    <C4FStatusContext.Provider value={c4fStatusValue}>
      <ReferenceDataContext.Provider value={referenceDataValue}>
        <LocalizationContextProvider>
          <Container className="container" style={{ backgroundColor: 'rgba(222, 233, 244, 1)' }}>
            {showBanner && !config.IS_TEST_ENVIRONMENT ?
              <Row className=" header">
                <img alt="banner" className="headerimg" src={header}></img>
              </Row>
              : null}
            {config.IS_TEST_ENVIRONMENT ? 
              <Row className="mt-2 justify-content-center">
                <Alert variant="danger">TEST ENVIRONMENT</Alert>
              </Row>
              : null }        
            <EventContext.Provider value={eventValue}>
              <AdminUserInformationContext.Provider value={adminUserValue}>
                <SitterUserInformationContext.Provider value={sitterUserValue}>
                  <UserInformationContext.Provider value={userValue}>
                    <PollContext.Provider value={pollValue}>
                      <PollModal key={'pollModal'}
                        show={showPollModal}
                        pollId={selectedPollId}
                        handleClose={() => setShowPollModal(false)} />
                      <InfoModalContext.Provider value={infoModalValue}>
                        {selectedInfoObject ?
                          <InfoModal key={'appInfoModal'}
                            show={showInfoModal}
                            selectedObject={selectedInfoObject}
                            handleClose={() => setShowInfoModal(false)} />
                          : null}
                        <Helmet>
                          <title>Cycling4Freaks - Online Cycling Game</title>
                          <meta name="description"
                            content="Cycling4Freaks - Free Cycling Manager Game - All races live! Against real opponents! Day after day the real cycling calendar! Build your team and lead it to victory! Cycling4Freaks is a fair, balanced play-to-win game." />
                          <meta name="keywords" content="cycling, simulation, manager, game, online, free"></meta>
                        </Helmet>
                        {error}
                        <Navigation></Navigation>
                        <Routes>
                          <Route exact path="/" element={<Start />} />
                          <Route path="/start" element={<Start />} />
                          <Route path="/c4f-2.0/c4f-react" element={<Start />} />
                          <Route path="/c4f-3.0/c4f-react" element={<Start />} />
                          <Route path="/c4f-2.0-beta/c4f-react" element={<Start />} />
                          <Route path="/c4f-3.0-beta/c4f-react" element={<Start />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/calendar" element={<Calendar />} />
                          <Route path="/sandbox" element={<Sandbox />} />
                          <Route path="/statistics" element={<Statistics />} />
                          <Route path="/signup" element={<Signup />} />
                          <Route path="/manual" element={<Manual />} />
                          <Route path="/raceEditor" element={
                            <ProtectedRoute user={userInformation}>
                              {sitterUserInformation === null ?
                                <RaceEditor />
                                : null}
                            </ProtectedRoute>
                          } />
                          <Route path="/team" element={
                            <ProtectedRoute user={userInformation}>
                              {sitterUserInformation === null ?
                                <Team />
                                : null}
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/inscription" element={
                            <ProtectedRoute user={userInformation}>
                              <Inscription />
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/race" element={
                            <ProtectedRoute user={userInformation}>
                              <RaceWrapper />
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/nc" element={
                            <ProtectedRoute user={userInformation}>
                              <NationalChampionships />
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/finance" element={
                            <ProtectedRoute user={userInformation}>
                              {sitterUserInformation === null ?
                                <Finance />
                                : null}
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/training" element={
                            <ProtectedRoute user={userInformation}>
                              <Training />
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/transfermarket" element={
                            <ProtectedRoute user={userInformation}>
                              {sitterUserInformation === null ?
                                <Transfer />
                                : null}
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/home" element={
                            <ProtectedRoute user={userInformation}>
                              <Home />
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/chat" element={
                            <ProtectedRoute user={userInformation}>
                              <Chat />
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/profile" element={
                            <ProtectedRoute user={userInformation}>
                              {sitterUserInformation === null ?
                                <Profile />
                                : null}
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/admin" element={
                            <ProtectedRoute user={userInformation}>
                              {sitterUserInformation === null ?
                                <Admin />
                                : null}
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/fairplay" element={
                            <ProtectedRoute user={userInformation}>
                              {sitterUserInformation === null ?
                                <FairplayCommittee />
                                : null}
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/nationsCommittee" element={
                            <ProtectedRoute user={userInformation}>
                              {sitterUserInformation === null ?
                                <NationsCommittee />
                                : null}
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/messages" element={
                            <ProtectedRoute user={userInformation}>
                              {sitterUserInformation === null ?
                                <Notifications />
                                : null}
                            </ProtectedRoute>}>
                          </Route>
                          <Route path="/messagesUnread" element={
                            <ProtectedRoute user={userInformation}>
                              {sitterUserInformation === null ?
                                <Notifications />
                                : null}
                            </ProtectedRoute>}>
                          </Route>
                        </Routes>
                      </InfoModalContext.Provider>
                    </PollContext.Provider>
                  </UserInformationContext.Provider>
                </SitterUserInformationContext.Provider>
              </AdminUserInformationContext.Provider>
            </EventContext.Provider>
          </Container>
        </LocalizationContextProvider>
      </ReferenceDataContext.Provider>
    </C4FStatusContext.Provider>

  );
}


export default App;
